import React, { Fragment, useContext, useEffect, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import { AppContext } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import * as Fi from "react-icons/fi";
import { Switch, Modal } from "antd";
import { toast } from "react-toastify";
import "../styles/style.css";

const MORE_CONTROLS = [
  {
    to: "/reservation-preferences",
    title: "Reservation preferences",
    description: "Approval and service settings",
    icon: Fi.FiSliders,
  },
  {
    to: "/reservation-timings",
    title: "Reservation timings",
    description: "Weekly opening hours and slots",
    icon: Fi.FiClock,
  },
  {
    to: "/dining-tables",
    title: "Dining tables",
    description: "Add and manage seating tables",
    icon: Fi.FiGrid,
  },
  {
    to: "/reservation-closures",
    title: "Closures",
    description: "Temporary restaurant closures",
    icon: Fi.FiCalendar,
  },
];

function ReservationSettings() {
  const navigate = useNavigate();
  const {
    tokenData,
    reservationStatus,
    foodpageReservSettings,
    reservSettingsLoading,
    updateSettings,
    getSettings,
  } = useContext(AppContext);

  const [warningModal, setWarningModal] = useState(false);
  const [reservStatus, setReservStatus] = useState(false);

  useEffect(() => {
    setReservStatus(reservationStatus === "Enabled");
  }, [reservationStatus]);

  const shopName = tokenData?.shopName || "Your restaurant";
  const shopMeta = [
    tokenData?.email,
    reservationStatus === "Enabled" ? "Reservations on" : "Reservations off",
  ]
    .filter(Boolean)
    .join(" · ");

  const handleChangeStatus = async () => {
    const alterValue = !reservStatus;
    const statusValue = alterValue ? "Enabled" : "Disabled";

    if (!foodpageReservSettings) {
      toast.error("Settings not available.");
      return;
    }

    const {
      updated_at,
      source,
      id,
      shopID,
      currencyDetails,
      ...rest
    } = foodpageReservSettings;

    const payload = {
      ...rest,
      reservationStatus: statusValue,
      source: "REACT",
    };

    await updateSettings(payload, {
      onSuccess: async () => {
        toast.info(`Reservation ${alterValue ? "Enabled" : "Disabled"}`);
        await getSettings();
        setWarningModal(false);
      },
      onFailed: (err) => {
        console.log("updated error", err);
        toast.error("Could not update reservation status.");
      },
    });
  };

  const closeWarningModal = () => setWarningModal(false);

  const statusMidFunction = () => {
    if (reservStatus === true) {
      setWarningModal(true);
      return;
    }
    handleChangeStatus();
  };

  return (
    <Fragment>
      <MasterLayout>
        <Modal
          open={warningModal}
          onClose={closeWarningModal}
          footer={null}
          onCancel={closeWarningModal}
        >
          <h4 className="sub_title_">Disable Table Reservation?</h4>
          <br />
          <p className="mt-4 fs-6">
            Disabling table reservations will make the reservation feature
            unavailable on the client site. Customers will not be able to
            reserve tables until you enable it again.
          </p>
          <hr />
          <br />
          <div className="d-flex">
            <button
              type="button"
              className="admin_btn"
              style={{ padding: "0.5rem 1.5rem" }}
              onClick={handleChangeStatus}
              disabled={reservSettingsLoading}
            >
              {reservSettingsLoading ? "Please wait!" : "Disable Reservation"}
            </button>
            <button
              type="button"
              className="btn btn-light ms-2 rounded-pill px-3"
              style={{ padding: "0.5rem 1.5rem" }}
              onClick={closeWarningModal}
            >
              Cancel
            </button>
          </div>
        </Modal>

        <div className="more-page">
          {/* <header className="more-page__header">
            <p className="more-page__eyebrow">Foodpage reservations</p>
            <h1 className="more-page__title">Your restaurant</h1>
            <p className="more-page__subtitle">
              Keep things running your way.
            </p>
          </header> */}

          <section className="more-page__basic" aria-label="Basic information">
            <p className="more-page__basic-label">Basic Information</p>
            <h2 className="more-page__basic-name">{shopName}</h2>
            <p className="more-page__basic-meta">
              {shopMeta || tokenData?.shopUrl || "Restaurant team"}
            </p>
            {(tokenData?.mobile || tokenData?.pluginDomain) && (
              <dl className="more-page__basic-details">
                {tokenData?.mobile && (
                  <div className="more-page__basic-row">
                    <dt>Mobile</dt>
                    <dd>{tokenData.mobile}</dd>
                  </div>
                )}
                {tokenData?.shopUrl && (
                  <div className="more-page__basic-row">
                    <dt>Shop URL</dt>
                    <dd>{tokenData.shopUrl}</dd>
                  </div>
                )}
                {tokenData?.pluginDomain && (
                  <div className="more-page__basic-row">
                    <dt>Plugin</dt>
                    <dd>
                      <a
                        href={tokenData.pluginDomain}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {tokenData.pluginDomain}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </section>

          <section
            className={`more-page__status${reservStatus ? " is-on" : " is-off"}`}
            aria-label="Reservation status"
          >
            <div className="more-page__status-text">
              <span className="more-page__status-label">Reservation status</span>
              <span
                className={
                  reservStatus
                    ? "more-page__status-value is-on"
                    : "more-page__status-value is-off"
                }
              >
                {reservStatus
                  ? "Guests can book tables"
                  : "Bookings are paused"}
              </span>
            </div>
            <Switch
              onChange={statusMidFunction}
              checked={reservStatus}
              size="default"
              disabled={reservSettingsLoading || !foodpageReservSettings}
            />
          </section>

          <p className="more-page__section-label">Manage</p>
          <nav className="more-page__controls" aria-label="More options">
            {MORE_CONTROLS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.to}
                  type="button"
                  className="more-page__control"
                  onClick={() => navigate(item.to)}
                >
                  <span className="more-page__control-icon" aria-hidden>
                    <Icon />
                  </span>
                  <span className="more-page__control-text">
                    <span className="more-page__control-title">
                      {item.title}
                    </span>
                    <span className="more-page__control-desc">
                      {item.description}
                    </span>
                  </span>
                  <Fi.FiChevronRight
                    className="more-page__control-chevron"
                    aria-hidden
                  />
                </button>
              );
            })}
          </nav>
        </div>
      </MasterLayout>
    </Fragment>
  );
}

export default ReservationSettings;
